import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import {
  ItemCardBody,
  ItemCardMeta,
  ItemCardSection,
  ItemCardSummary,
} from '@sola/ark-core/molecules/item-card/item-card.tsx';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import {
  getCommitmentLabel,
  getCommitmentTitle,
  getFrequencyAbbr,
} from '@sola/supporter-sdk/utils/commitments.ts';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.tsx';

const useComponentData = (args: {
  commitment: GraphQLCommitment;
  t: TFunction;
}) => {
  const { commitment, t } = args;

  const icon = <CommitmentAvatar responsive commitment={commitment} />;
  const title = getCommitmentTitle(commitment, t);
  const label = getCommitmentLabel(commitment, t);

  const rate = t('numbers.currencyCustom', {
    amount: commitment?.rate,
    useNegativeParens: true,
  });

  const missedAmount =
    (commitment.dueThisMonthAmount || 0) +
    (commitment.missedPaymentAmount || 0);
  return {
    totalMissedAmount:
      missedAmount > 0
        ? t('numbers.currencyCustom', {
            amount: missedAmount,
            useNegativeParens: true,
          })
        : undefined,

    hasMissedPayment: commitment.hasMissedPayment,
    frequencyAbbr: commitment?.frequency
      ? getFrequencyAbbr(commitment.frequency, t)
      : undefined,
    icon,
    title,
    label,
    rate,
  };
};

export interface CommitmentCardInfoSectionProps {
  commitment: GraphQLCommitment;
  useRate?: boolean;
}

export const CommitmentCardInfoSection: React.FC<
  CommitmentCardInfoSectionProps
> = ({ commitment, useRate = true }) => {
  const { t } = useTranslation(['my']);
  const {
    icon,
    title,
    label,
    rate,
    frequencyAbbr,
    hasMissedPayment,
    totalMissedAmount,
  } = useComponentData({ t, commitment });

  return (
    <ItemCardSection>
      <ItemCardBody>
        <ItemCardSummary icon={icon} title={title} subTitle={label} />
        <ItemCardMeta
          text={useRate ? rate : totalMissedAmount}
          textContrast={useRate ? frequencyAbbr : undefined}
          badge={
            hasMissedPayment ? (
              <Badge
                label={t('my:commitments.missedPaymentsLabel')}
                color="negative"
                responsive
              />
            ) : undefined
          }
        />
      </ItemCardBody>
    </ItemCardSection>
  );
};
